import type { Course } from '@/types/composition-functions';

/**
 * A tertiary course "instance" is one offering of a course code in one year.
 * The backend keys it with `course_instance_id`, a composite string of the form
 * `<COURSE CODE>*0109<YEAR>*<school_id>` (e.g. `CSC101*01092025*17`). That key
 * is an identifier, not a label: screens should show "CSC101 · 2025" instead.
 *
 * `course_instance_year` is the year as the API now exposes it (contract C2).
 * Forms hold it as a string before saving, so both shapes are accepted; the
 * parser below is the fallback for documents that still only carry the key.
 */
type Nullable<T> = { [K in keyof T]?: T[K] | null };

export type CourseInstanceLike = Nullable<Pick<Course, 'course_code' | 'course_instance_id'>> & {
  course_instance_year?: number | string | null;
};

const INSTANCE_KEY_YEAR = /\*0109(\d{4})\*\d+$/;

function yearFromValue(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const year = Number(value);
  return Number.isInteger(year) && year > 0 ? year : null;
}

/** The instance year encoded in a `course_instance_id` key, or null when the key has none. */
export function yearFromCourseInstanceId(instanceId: string | null | undefined): number | null {
  if (!instanceId) return null;
  const match = INSTANCE_KEY_YEAR.exec(instanceId.trim());
  return match ? Number(match[1]) : null;
}

/**
 * The year a course instance belongs to: the explicit `course_instance_year`
 * when the document selected it, else whatever the composite key encodes.
 */
export function courseInstanceYear(course: CourseInstanceLike | string | null | undefined): number | null {
  if (course === null || course === undefined) return null;
  if (typeof course === 'string') return yearFromCourseInstanceId(course);
  return yearFromValue(course.course_instance_year) ?? yearFromCourseInstanceId(course.course_instance_id);
}

/**
 * Human label for a course instance: "CSC101 · 2025". Falls back to the code
 * alone when no year is known, to the year alone when no code is known, and to
 * an empty string when neither is — so templates can `|| '—'`.
 */
export function formatCourseInstance(course: CourseInstanceLike | null | undefined): string {
  if (!course) return '';
  const code = (course.course_code ?? '').trim();
  const year = courseInstanceYear(course);
  if (code && year !== null) return `${code} · ${year}`;
  if (code) return code;
  return year === null ? '' : String(year);
}

/**
 * Heading for a course-equivalence group: the member codes joined with ≡
 * ("CSC101 ≡ CSC111"). The group's internal sequence number means nothing to
 * a user, so it is never shown.
 */
export function courseEquivalenceLabel(courses: ReadonlyArray<Pick<Course, 'course_code'>>): string {
  const codes = courses.map((c) => c.course_code.trim()).filter(Boolean);
  if (codes.length === 0) return 'Empty group';
  return codes.join(' ≡ ');
}
